import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton } from '../../components/mui/Buttons/Buttons';
import useHandleApiResponse from '../../Hooks/useHandleApiResponse';
import { getWebinars, updateWebinarStatus } from '../../redux/features/webinarSlice';
import { WebinarTableHeader } from './components/WebinarTableHeader';
import WebinarAddEdit from './components/WebinarAddEdit';
import DeleteWebinar from './components/DeleteWebinar';
import ActiveConflictAlert, {
  isActiveConflict,
  activeConflictName,
} from './components/ActiveConflictAlert';

const Webinar = () => {
  const dispatch = useDispatch();
  const { data, meta, loading } = useSelector((state) => state.webinar.table);
  const { mutating } = useSelector((state) => state.webinar);
  const handleApiResponse = useHandleApiResponse();

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    sort: { field: '', direction: 'asc' },
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const [modalState, setModalState] = useState({ actionKey: '', row: null });

  // The "two active webinars" refusal from the API, held so the alert box can
  // repeat the server's own wording rather than inventing its own.
  const [conflict, setConflict] = useState({ open: false, message: '', name: null });

  // Params the LIST endpoint understands (see docs/webinar-api.md).
  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
      sortBy: tableState.sort?.field || undefined,
      sortDirection: tableState.sort?.direction || 'asc',
    }),
    [tableState.page, tableState.pageSize, tableState.search, tableState.sort],
  );

  useEffect(() => {
    dispatch(getWebinars(dispatchParams));
  }, [dispatch, dispatchParams]);

  const handleRefresh = useCallback(() => {
    dispatch(getWebinars({ ...dispatchParams, hardRefresh: true }));
  }, [dispatch, dispatchParams]);

  // Close a modal; refetch when a mutation succeeded.
  const closeModal = useCallback(
    (shouldRefresh = false) => {
      setModalState({ actionKey: '', row: null });
      if (shouldRefresh) handleRefresh();
    },
    [handleRefresh],
  );

  // The grid switch. ALWAYS refetches, success or not — the switch is optimistic
  // until the server answers, so a refusal has to put the row back the way it was.
  //
  // Activating while another webinar is active is REFUSED by the API, not merged.
  // That refusal gets an alert box instead of the usual snackbar: it is a rule the
  // user has to act on, not a transient failure, and a snackbar on a wide grid
  // gets missed. Every other failure still goes through handleApiResponse.
  const handleToggleActive = useCallback(
    async (row, nextValue) => {
      if (!row?.webinar_id) return;
      const res = await dispatch(
        updateWebinarStatus({ webinarId: row.webinar_id, isActive: nextValue }),
      );

      if (isActiveConflict(res)) {
        setConflict({
          open: true,
          message: res?.payload?.message,
          name: activeConflictName(res),
        });
        handleRefresh(); // put the switch back
        return;
      }

      handleApiResponse(res, {
        onSuccess: handleRefresh,
        onFailed: handleRefresh,
      });
    },
    [dispatch, handleApiResponse, handleRefresh],
  );

  const headers = useMemo(
    () => WebinarTableHeader({ onToggleActive: handleToggleActive, busy: mutating }),
    [handleToggleActive, mutating],
  );

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;
    setModalState({ actionKey, row });
  }, []);

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'addWebinar',
        title: 'Add Webinar',
        render: () => <WebinarAddEdit onClose={closeModal} />,
      },
      {
        actionKey: 'editWebinar',
        title: 'Edit Webinar',
        render: ({ row }) => <WebinarAddEdit row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'deleteWebinar',
        title: 'Delete Webinar',
        render: ({ row }) => <DeleteWebinar row={row} onClose={closeModal} />,
      },
    ],
    [closeModal],
  );

  const activeConfig = useMemo(
    () => modalConfigs.find((config) => config.actionKey === modalState.actionKey),
    [modalConfigs, modalState.actionKey],
  );

  return (
    <GlobalWrapper title={'Webinar'}>
      <Box mt={1}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <AddButton action={() => setModalState({ actionKey: 'addWebinar', row: null })}>
            Add Webinar
          </AddButton>
        </Stack>

        <Paper elevation={3}>
          <Box mt={1}>
            <GlobalTable
              rowUniqueId="webinar_id"
              headers={headers}
              tableState={tableState}
              setTableState={setTableState}
              loading={loading}
              rows={data}
              meta={meta}
              onButtonActionClick={handleButtonActionClick}
              exportUrl="webinar"
              exportParams={{ ...dispatchParams, page: 1, limit: 'no' }}
            />
          </Box>

          <GlobalDialog
            title={activeConfig?.title}
            open={Boolean(modalState?.actionKey)}
            setOpen={(val) => {
              if (!val) setModalState({ actionKey: '', row: null });
            }}
            closeButton={true}
            content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
            maxWidth={'sm'}
            fullWidth={true}
          />
        </Paper>

        <ActiveConflictAlert
          open={conflict.open}
          message={conflict.message}
          conflictName={conflict.name}
          onClose={() => setConflict({ open: false, message: '', name: null })}
        />
      </Box>
    </GlobalWrapper>
  );
};

export default Webinar;
